import React from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  useTheme, useMediaQuery, IconButton, Typography, Avatar, Toolbar, AppBar,
  Menu, MenuItem, Divider, Tooltip
} from '@mui/material';
import {
  SpaceDashboardRounded, CategoryRounded, Inventory2Rounded, LogoutRounded, ShoppingCartRounded,
  ReceiptLongRounded, HelpOutlineRounded, PolicyRounded, PeopleAltRounded, BusinessRounded,
  Menu as MenuIcon, StorefrontRounded, WebRounded, DashboardCustomizeRounded,
  AdminPanelSettingsRounded, KeyboardArrowDownRounded
} from '@mui/icons-material';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getRole, getAdminName, clearSession } from '../auth';
import api from '../api';
import { BRAND } from '../theme';

const drawerWidth = 264;

const NAV_SECTIONS = [
  {
    title: 'Overview',
    items: [
      { text: 'Dashboard', icon: <SpaceDashboardRounded />, path: '/dashboard', roles: ['superadmin', 'admin'] },
    ],
  },
  {
    title: 'Site builder',
    items: [
      { text: 'Template Category', icon: <DashboardCustomizeRounded />, path: '/templateCategory', roles: ['superadmin', 'admin'] },
      { text: 'Template',          icon: <WebRounded />,                path: '/template',         roles: ['superadmin', 'admin'] },
    ],
  },
  {
    title: 'Catalog',
    items: [
      { text: 'Categories', icon: <CategoryRounded />,   path: '/categories', roles: ['superadmin', 'admin'] },
      { text: 'Products',   icon: <Inventory2Rounded />, path: '/products',   roles: ['superadmin', 'admin'] },
      { text: 'Businesses', icon: <BusinessRounded />,   path: '/businesses', roles: ['superadmin', 'admin'] },
    ],
  },
  {
    title: 'Sales',
    items: [
      { text: 'Orders',      icon: <ReceiptLongRounded />,  path: '/orders',      roles: ['superadmin', 'admin'] },
      { text: 'Place Order', icon: <ShoppingCartRounded />, path: '/place-order', roles: ['superadmin', 'admin'] },
    ],
  },
  {
    title: 'People',
    items: [
      { text: 'Users',  icon: <PeopleAltRounded />,          path: '/users',  roles: ['superadmin', 'admin'] },
      { text: 'Admins', icon: <AdminPanelSettingsRounded />, path: '/admins', roles: ['superadmin'] },
    ],
  },
  {
    title: 'Content',
    items: [
      { text: 'FAQ',            icon: <HelpOutlineRounded />, path: '/faq',            roles: ['superadmin', 'admin'] },
      { text: 'Privacy Policy', icon: <PolicyRounded />,      path: '/privacy-policy', roles: ['superadmin', 'admin'] },
    ],
  },
];

const ALL_ITEMS = NAV_SECTIONS.flatMap(s => s.items.map(i => ({ ...i, section: s.title })));
const PAGE_INFO = Object.fromEntries(ALL_ITEMS.map(m => [m.path, m]));

const initials = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || 'A';

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const role = getRole();
  const adminName = getAdminName();
  const roleLabel = role === 'superadmin'
    ? 'Super Admin'
    : localStorage.getItem('templateName') || 'Admin';
  const sections = NAV_SECTIONS
    .map(s => ({ ...s, items: s.items.filter(i => i.roles.includes(role)) }))
    .filter(s => s.items.length);
  const page = PAGE_INFO[location.pathname] || { text: 'Admin', section: 'Overview' };

  const handleLogout = async () => {
    setMenuAnchor(null);
    // Best effort: the backend logout is stateless, so a failure must not block signing out.
    await api.post('/api/admins/logout').catch(() => {});
    clearSession();
    navigate('/login', { replace: true });
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
      {/* Brand */}
      <Box sx={{ px: 3, height: 72, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2.5, background: BRAND.gradient, boxShadow: BRAND.glow,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <StorefrontRounded sx={{ color: '#fff', fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>Salesmart</Typography>
          <Typography noWrap sx={{ color: 'text.secondary', fontSize: 11.5 }}>{roleLabel}</Typography>
        </Box>
      </Box>

      {/* Nav */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {sections.map(section => (
          <Box key={section.title} sx={{ mt: 2 }}>
            <Typography sx={{
              px: 1.5, mb: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: 1.2,
              textTransform: 'uppercase', color: '#a7abbd',
            }}>
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map(item => {
                const active = location.pathname === item.path;
                return (
                  <ListItemButton
                    key={item.path}
                    component={Link}
                    to={item.path}
                    onClick={() => isMobile && setMobileOpen(false)}
                    sx={{
                      borderRadius: 999,
                      mb: 0.5,
                      px: 1.75,
                      py: 0.85,
                      color: active ? '#fff' : 'text.primary',
                      background: active ? BRAND.gradient : 'transparent',
                      boxShadow: active ? BRAND.glow : 'none',
                      transition: 'background-color .15s, color .15s',
                      '&:hover': {
                        background: active ? BRAND.gradient : '#f2f1ff',
                        color: active ? '#fff' : BRAND.violet,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: 'inherit', '& svg': { fontSize: 20 } }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      slotProps={{ primary: { fontSize: 13.5, fontWeight: active ? 600 : 500 } }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Logout */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 999, px: 1.75, py: 0.85, color: 'text.secondary',
            '&:hover': { bgcolor: '#fff0f2', color: 'error.main' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34, color: 'inherit', '& svg': { fontSize: 20 } }}>
            <LogoutRounded />
          </ListItemIcon>
          <ListItemText primary="Logout" slotProps={{ primary: { fontSize: 13.5, fontWeight: 500 } }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main */}
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'saturate(180%) blur(12px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
          }}
        >
          <Toolbar sx={{ gap: 1.5, minHeight: { xs: 64, md: 72 }, px: { xs: 2, md: 4 } }}>
            {isMobile && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.2 }}>
                {page.section}
              </Typography>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 19, lineHeight: 1.3 }}>
                {page.text}
              </Typography>
            </Box>
            <Tooltip title="Account">
              <Box
                component="button"
                onClick={e => setMenuAnchor(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: 'divider',
                  bgcolor: '#fff', borderRadius: 999, pl: 0.5, pr: 1.25, py: 0.5, cursor: 'pointer',
                  font: 'inherit', color: 'inherit',
                }}
              >
                <Avatar sx={{ width: 32, height: 32, background: BRAND.gradient, fontSize: 13, fontWeight: 700 }}>
                  {initials(adminName)}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>{adminName}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>{roleLabel}</Typography>
                </Box>
                <KeyboardArrowDownRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Box>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { mt: 1, minWidth: 200, borderRadius: '14px', boxShadow: BRAND.softShadow } } }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{adminName}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {localStorage.getItem('adminEmail') || roleLabel}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ fontSize: 13.5, color: 'error.main', gap: 1 }}>
                <LogoutRounded fontSize="small" /> Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
